import { JobExecutionStatus, JobStatus } from '@stratumn/jobs';
import { Check, Pause, TriangleAlert } from 'lucide-react';
import theme from '@/styles/theme';

import { GlobalETLStatus } from './types';

export const getStatusColor = status => {
  switch (status) {
    case JobStatus.ACTIVE:
      return 'bg-green-100';
    case JobStatus.PAUSED:
      return 'bg-yellow-100';
    default:
      return theme.color.grey[3];
  }
};

export const getIconColors = (iconStatus: GlobalETLStatus) => {
  let bgColor: string, color: string;
  switch (iconStatus) {
    case GlobalETLStatus.HEALTHY:
      bgColor = 'bg-green-100';
      color = '#8BD2C1';
      break;
    case GlobalETLStatus.UNHEALTHY:
      bgColor = 'bg-red-100';
      color = '#F58388';

      break;
    case GlobalETLStatus.NOT_RUNNING:
    default:
      bgColor = 'bg-gray-100';
      color = 'text-gray-500';

      break;
  }

  return { bgColor, color };
};

export const getIconStatus = (
  jobStatus: JobStatus,
  jobExecutionStatus?: JobExecutionStatus
): GlobalETLStatus => {
  if (jobStatus === JobStatus.PAUSED) {
    return GlobalETLStatus.NOT_RUNNING;
  }

  if (jobExecutionStatus === JobExecutionStatus.FAILED) {
    return GlobalETLStatus.UNHEALTHY;
  }
  // Job is In progress & No pods yet OR pod is SUCESS
  return GlobalETLStatus.HEALTHY;
};

export const statusIconMap = {
  [GlobalETLStatus.HEALTHY]: Check,
  [GlobalETLStatus.UNHEALTHY]: TriangleAlert,
  [GlobalETLStatus.NOT_RUNNING]: Pause
};

export const validateFiltersInput = (input: string): boolean => {
  const regex = /^[a-zA-Z,]*$/;
  return regex.test(input);
};

export const validateSnakeCase = (input: string): boolean => {
  const regex = /^[a-z0-9_]*$/;
  return regex.test(input);
};
const UNITS_TO_SECONDS = {
  SECOND: 1,
  MINUTE: 60,
  HOUR: 3600,
  DAY: 86_400,
  MONTH: 2_592_000, // 30 days
  YEAR: 31_536_000 // 365 days
};

export const formatSecondsToHumanDuration = (duration: number): string => {
  if (duration < UNITS_TO_SECONDS.MINUTE) {
    return `${duration}s`;
  }
  if (duration < UNITS_TO_SECONDS.HOUR) {
    return `${Math.floor(duration / UNITS_TO_SECONDS.MINUTE)}m`;
  }
  if (duration < UNITS_TO_SECONDS.DAY) {
    return `${Math.floor(duration / UNITS_TO_SECONDS.HOUR)}h`;
  }
  if (duration < UNITS_TO_SECONDS.MONTH) {
    return `${Math.floor(duration / UNITS_TO_SECONDS.DAY)}d`;
  }
  if (duration < UNITS_TO_SECONDS.YEAR) {
    return `${Math.floor(duration / UNITS_TO_SECONDS.MONTH)}mo`;
  }

  return `${Math.floor(duration / UNITS_TO_SECONDS.YEAR)}y`;
};

export const getDuration = (
  executionTime?: string,
  lastUpdatedTime?: string | null
) => {
  const executionTimeDate = new Date(executionTime ?? '');
  const lastUpdatedTimeDate = lastUpdatedTime
    ? new Date(lastUpdatedTime)
    : executionTimeDate;

  const duration = lastUpdatedTimeDate.getTime() - executionTimeDate.getTime();
  const durationInSeconds = Math.floor(duration / 1000);

  return formatSecondsToHumanDuration(durationInSeconds);
};

export const getElapsedTime = (executionTime?: string) => {
  const now = new Date();
  const timeElapsed = now.getTime() - new Date(executionTime ?? '').getTime();
  const timeElapsedInSeconds = Math.floor(timeElapsed / 1000);

  return formatSecondsToHumanDuration(timeElapsedInSeconds).concat(' ago');
};
